/**
 * The two moments of a search-attributed conversion:
 *
 * 1. the user clicks a result — the click event goes out and the attribution
 *    context for that course is stored ({@link sendHitClickEvent});
 * 2. the user completes an action of value — the conversion for *that* course
 *    goes out ({@link sendConversion}).
 *
 * There is no third step. The context used to be a single slot that the landing
 * page had to invalidate when it did not match; now each course keeps its own
 * entry and the conversion looks up the one it is converting, so there is
 * nothing to invalidate and no route that can forget to do it.
 *
 * Every function is best-effort: an analytics failure must never interrupt a
 * purchase or an application.
 */
import { canSendInsights, configuredCoursesIndex, contextCookieOptions, insightsClient } from './client';
import { INSIGHT_CONTEXT_TTL_MS, clearInsightContext, loadInsightContext, loadMostRecentInsightContext, normalizeCourseCode, removeInsightContext, saveInsightContext } from './context';
/** Default name of the click event, kept stable for the Algolia dashboard. */
const DEFAULT_CLICK_EVENT = 'Course card clicked';
/** Set once so a misconfigured page does not log on every single click. */
let warnedMissingQueryID = false;
/** Set once so a mis-indexed `courseCode` does not log on every single click. */
let warnedMissingCourseCode = false;
/**
 * Reads a string attribute off a hit.
 *
 * @param hit - The Algolia hit.
 * @param key - Attribute name.
 * @returns The value, or `undefined` when absent or not a string.
 */
function hitString(hit, key) {
    const value = hit[key];
    return typeof value === 'string' && value ? value : undefined;
}
/**
 * Warns once that a hit carries no `courseCode`.
 *
 * Without it the entry could never be looked up at conversion time, so it is
 * not written at all — but a silently missing index attribute would otherwise
 * show up only as an unexplained drop in conversion rate.
 */
function warnMissingCourseCode() {
    if (warnedMissingCourseCode)
        return;
    warnedMissingCourseCode = true;
    console.warn('[algolia-insights] hit without courseCode: the click is sent but the conversion that follows cannot be attributed.');
}
/**
 * Sends a click event and stores the attribution context for that course.
 *
 * Every Algolia-backed surface does both: a search page, a category listing and
 * the quiz results are all searches that returned a `queryID`, and withholding
 * their conversions would leave Dynamic Re-Ranking re-ordering those pages on
 * click popularity alone — engagement without outcome. The surfaces are told
 * apart in the reports by their `analyticsTags`, not by suppressing events.
 *
 * @param params - The hit, the index, and the widget's `sendEvent`.
 */
export function sendHitClickEvent(params) {
    if (!canSendInsights())
        return;
    try {
        const { hit, indexName, sendEvent, eventName = DEFAULT_CLICK_EVENT } = params;
        const queryID = hitString(hit, '__queryID');
        const objectID = hitString(hit, 'objectID');
        if (!queryID || !objectID) {
            if (!warnedMissingQueryID) {
                warnedMissingQueryID = true;
                console.warn('[algolia-insights] hit without __queryID/objectID: the search is missing clickAnalytics, clicks will not be attributable.');
            }
            return;
        }
        sendEvent('click', [hit], eventName);
        const courseCode = hitString(hit, 'courseCode');
        if (!courseCode) {
            warnMissingCourseCode();
            return;
        }
        saveInsightContext({ queryID, objectID, courseCode, index: indexName }, contextCookieOptions());
    }
    catch (error) {
        console.warn('[algolia-insights] click event failed:', error);
    }
}
/**
 * Sends a click for a surface that is not driven by InstantSearch — the navbar
 * and hero search preview, which fetch through `searchPreview()` and therefore
 * have no `sendEvent` to route through, and the profiler's Recommend-based
 * fallback suggestions, which have no query at all.
 *
 * Picks the attributed variant when the search carried a `queryID`, and the
 * plain one otherwise. The caller never chooses: an `…AfterSearch` event
 * without a `queryID` is an API error.
 *
 * @param params - The record, the index, and the query that produced it.
 */
export function sendDirectClickEvent(params) {
    if (!canSendInsights())
        return;
    try {
        const { objectID, indexName, queryID, position, eventName = DEFAULT_CLICK_EVENT, courseCode } = params;
        if (!objectID)
            return;
        if (queryID && position !== undefined) {
            insightsClient()('clickedObjectIDsAfterSearch', {
                eventName,
                index: indexName,
                objectIDs: [objectID],
                positions: [position],
                queryID
            });
            if (!courseCode) {
                warnMissingCourseCode();
                return;
            }
            saveInsightContext({ queryID, objectID, courseCode, index: indexName }, contextCookieOptions());
            return;
        }
        insightsClient()('clickedObjectIDs', {
            eventName,
            index: indexName,
            objectIDs: [objectID]
        });
    }
    catch (error) {
        console.warn('[algolia-insights] click event failed:', error);
    }
}
/**
 * Insights method per subtype, in both variants.
 *
 * All six are `eventType: 'conversion'`. The `…AfterSearch` half carries a
 * `queryID` and is what Dynamic Re-Ranking and Revenue analytics read; the
 * plain half carries none and feeds Personalization and Recommend, which work
 * off `userToken` + `objectID` and never look at the query.
 */
const CONVERSION_METHOD = {
    attributed: {
        addToCart: 'addedToCartObjectIDsAfterSearch',
        purchase: 'purchasedObjectIDsAfterSearch',
        plain: 'convertedObjectIDsAfterSearch'
    },
    unattributed: {
        addToCart: 'addedToCartObjectIDs',
        purchase: 'purchasedObjectIDs',
        plain: 'convertedObjectIDs'
    }
};
/** Set once so a missing index configuration does not log on every conversion. */
let warnedMissingIndex = false;
/**
 * Finds the attribution context this conversion belongs to.
 *
 * @param options - The conversion options.
 * @returns The matching context, or `null` when the conversion is not
 *          attributable to a search.
 */
function resolveConversionContext(options) {
    if (options.courseCode)
        return loadInsightContext({ courseCode: options.courseCode });
    if (options.allowUnkeyedFallback)
        return loadMostRecentInsightContext();
    return null;
}
/**
 * Builds the target for a conversion with no search behind it.
 *
 * Relies on the courses index using the course code as its `objectID`, so a
 * conversion can name its own record without ever having been clicked from a
 * search. The code is sent **verbatim**, not upper-cased: the normalisation
 * applies to the lookup key, while Algolia wants the `objectID` exactly as the
 * index spells it.
 *
 * @param options - The conversion options.
 * @returns The target, or `null` when the course or the index is unknown.
 */
function unattributedTarget(options) {
    const courseCode = options.courseCode?.trim();
    if (!courseCode)
        return null;
    const index = configuredCoursesIndex();
    if (!index) {
        if (!warnedMissingIndex) {
            warnedMissingIndex = true;
            console.warn('[algolia-insights] no coursesIndex configured: conversions that did not come from a search cannot be sent.');
        }
        return null;
    }
    return { objectID: courseCode, index, courseCode: normalizeCourseCode(courseCode) };
}
/**
 * Sends a conversion for a course.
 *
 * Attributed to the search behind it when that course was clicked from one,
 * and plain otherwise — the variant is derived here, never chosen by the
 * caller, because an `…AfterSearch` event without a `queryID` is an API error
 * and the call site has no way to know whether the entry survived.
 *
 * Sending the plain variant matters: the `queryID` is what Dynamic Re-Ranking
 * and Revenue analytics need, but Personalization and Recommend do not use it,
 * and Recommend is what powers the profiler's zero-result fallback. Staying
 * silent on every conversion that did not follow a search within the window
 * would train it on a fraction of the purchases.
 *
 * **What happens to the attribution entry afterwards depends on where in the
 * funnel the event sits**, and getting this wrong silently loses revenue
 * attribution:
 *
 * - `addToCart` is a step, not an end. It leaves the entry alone, or the
 *   purchase it leads to — on another page, in another app — would have
 *   nothing left to attribute.
 * - `purchase` ends that course's funnel and consumes **its** entry, leaving
 *   the other courses the visitor is still weighing untouched.
 * - A plain conversion (brochure, application) also leaves the entry, since
 *   the same visitor may still buy. It is deduplicated instead, so a page
 *   refresh does not send it twice.
 *
 * @param eventName - Conversion name, e.g. `'Order placed'`.
 * @param options - The course being converted, plus subtype and revenue data.
 */
export function sendConversion(eventName, options = {}) {
    if (!canSendInsights())
        return;
    try {
        const context = resolveConversionContext(options);
        const target = context ?? unattributedTarget(options);
        if (!target)
            return;
        const { subtype, objectData, value, currency } = options;
        // `addToCart` is the only repeatable one: putting the same course in the
        // cart twice is a real user action worth recording. A plain conversion must
        // not repeat on a refresh, and a purchase must never double-count — its
        // entry being consumed is the primary defence, but a cookie write can fail,
        // and `payment-result` URLs are bookmarkable for as long as the window is
        // open.
        const deduplicated = subtype !== 'addToCart';
        const guardKey = conversionGuardKey(target, eventName);
        if (deduplicated && wasConversionSent(guardKey))
            return;
        const methods = target.queryID ? CONVERSION_METHOD.attributed : CONVERSION_METHOD.unattributed;
        const revenue = {
            ...(value !== undefined ? { value } : {}),
            ...(currency !== undefined ? { currency } : {})
        };
        try {
            if (subtype === 'purchase') {
                // The purchase variant deprecates the top-level `queryID` in favour of
                // a per-line `objectData[].queryID` (search-insights 2.17.3,
                // `dist/conversion.d.ts` — `InsightsSearchPurchaseEvent`).
                const line = {
                    ...(target.queryID ? { queryID: target.queryID } : {}),
                    ...(objectData?.[0] ?? {})
                };
                insightsClient()(methods.purchase, {
                    eventName,
                    index: target.index,
                    objectIDs: [target.objectID],
                    ...(Object.keys(line).length ? { objectData: [line] } : {}),
                    ...revenue
                });
            }
            else {
                insightsClient()(subtype ? methods.addToCart : methods.plain, {
                    eventName,
                    index: target.index,
                    objectIDs: [target.objectID],
                    ...(target.queryID ? { queryID: target.queryID } : {}),
                    ...(objectData?.length ? { objectData } : {}),
                    ...revenue
                });
            }
        }
        finally {
            // In a `finally` so a throwing client cannot leave the entry alive to
            // fire again on the next visit to a bookmarkable payment-result URL.
            if (subtype === 'purchase' && context) {
                removeInsightContext(context.courseCode, contextCookieOptions());
            }
        }
        if (deduplicated)
            markConversionSent(guardKey);
    }
    catch (error) {
        console.warn('[algolia-insights] conversion event failed:', error);
    }
}
/** localStorage key prefix for the per-conversion guard. */
const SENT_PREFIX = 'algolia_insights_sent:';
/**
 * Key identifying one conversion that must not be sent twice.
 *
 * Keyed on the course, **never on the `queryID`**. The first `purchase`
 * consumes the entry, so the reload of a bookmarkable `payment-result` URL
 * resolves the same order to the unattributed variant: a key carrying the
 * `queryID` would read `none:…` the second time, miss its own mark and send the
 * purchase again, double-counting the revenue. The two variants of one order
 * have to collide here — that is the whole point of the guard.
 *
 * The course is part of it because a single SERP hands out one `queryID` for
 * every hit on it: keyed on the query alone, downloading the brochure of the
 * second and third course opened from one search would be suppressed as
 * duplicates of the first — exactly the conversions the per-course context
 * exists to capture.
 *
 * @param target - What the conversion is about. Its `courseCode` is normalised,
 *                 so the attributed and unattributed spellings of the same
 *                 course cannot drift apart.
 * @param eventName - The conversion name.
 * @returns The storage key.
 */
function conversionGuardKey(target, eventName) {
    return `${SENT_PREFIX}${target.courseCode}:${eventName}`;
}
/**
 * Whether this conversion already went out.
 *
 * Kept in `localStorage`, not `sessionStorage`: the attribution window is a
 * week, so a guard that died with the tab would let the same visitor re-send
 * the same conversion days later, and Algolia does not deduplicate server-side.
 *
 * @param key - The guard key.
 * @returns `true` when it was already sent inside the attribution window.
 */
function wasConversionSent(key) {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw)
            return false;
        const sentAt = Number(raw);
        if (!Number.isFinite(sentAt))
            return false;
        return Date.now() - sentAt <= INSIGHT_CONTEXT_TTL_MS;
    }
    catch {
        // Storage unavailable: better a possible duplicate than a lost conversion.
        return false;
    }
}
/**
 * Records that this conversion went out, and prunes the marks that have outlived
 * the attribution window.
 *
 * @param key - The guard key.
 */
function markConversionSent(key) {
    try {
        pruneConversionGuards();
        window.localStorage.setItem(key, String(Date.now()));
    }
    catch {
        // ignore
    }
}
/** Drops guard marks older than the attribution window. */
function pruneConversionGuards() {
    try {
        const now = Date.now();
        const stale = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (!key?.startsWith(SENT_PREFIX))
                continue;
            const sentAt = Number(window.localStorage.getItem(key));
            if (!Number.isFinite(sentAt) || now - sentAt > INSIGHT_CONTEXT_TTL_MS)
                stale.push(key);
        }
        for (const key of stale)
            window.localStorage.removeItem(key);
    }
    catch {
        // ignore
    }
}
/** Drops every attribution context. */
export function clearAlgoliaInsightContext() {
    clearInsightContext(contextCookieOptions());
}
/** Resets the one-shot warnings. Test-only. */
export function resetInsightsWarningsForTests() {
    warnedMissingQueryID = false;
    warnedMissingCourseCode = false;
    warnedMissingIndex = false;
}
